import { atom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { IConversation, IConversationDetails } from "models/conversations"
import { VoipCallInitedBy, VoipCallStatus } from "models/voipCall"
import { startTransition } from "react"

/* --------------------------- Tipos auxiliares --------------------------- */

export interface IMessage {
  id: string
  _id?: string
  tempId: string
  attempts: number
  Call?: any
  operatorName: string
  contactName?: string
  isDeleted: boolean
  isForwarding: boolean
  Operator: {
    avatar?: string
    id: string
    name: string
  }
  callId: string
  content: string
  createdAt: string
  isMedia: boolean
  isSent: boolean
  isPinned: boolean
  error?: string
  type: number
  externalId?: string
  fileName?: string
  size?: number
  operatorId?: string
  replyTo?: any
  replyToMeta: {
    id: string
    _id: string
    createdAt: string
  }
  extraDataMessage?: IMessageExtraData
  errorDescription?: string
  customerPlatformId?: string
  idMessage?: string
  extra?: {
    edited?: boolean
    editions?: [
      {
        editedAt: string
        content: any
      },
    ]
    campaignId?: string
    campaignName?: string
    participantPhone?: string
    transcription?: string
    reaction?: {
      operatorId: string
      text: string
    }
    menu?: {
      contentText: string
      menu_options: {
        value: string
        name: string
      }[]
      type: "menu"
    }
    voip?: {
      id: string
      status: VoipCallStatus
      finishedAt: string
      minutes: number
      initedBy: VoipCallInitedBy
      voipCallId: string
      duration: number
    }
  }
  status?: number
}

interface IMessageExtraData {
  name?: string
  logo?: string
  permalink?: string
  phone?: string
  email?: string
  embed_conversation_id?: string
  embed_channel_id?: string
  type: "channel_transfer"
  operator_id: string
  target_conversation_id: string
}

export interface ICall {
  id: string
  answeredAt: string
  callExpiresAt: string
  chatBotId: string
  createdAt: string
  customerPlatformId: string
  extraData: any
  finishedAt: string
  groupId: string
  isChatBotActive: boolean
  isFinished: boolean
  isFromIntegration: boolean
  isHidden: boolean
  isImported: boolean
  isNew: boolean
  isReopened: boolean
  isWithoutOperatorAnswer: boolean
  labelId: string
  lastCallMessageId: string
  operatorId: string
  protocol: string
  workspaceId: string
  workspacePlatformId: string
}

export interface IConversationExtended extends IConversation {
  messages?: IMessage[]
  details?: IConversationDetails
  customFields?: {
    [key: string]: {
      value: string
    }
  }

  animated?: boolean
  /** Campos apenas do cliente (NÃO vêm do backend) */
  _clientOrder?: number // incrementa em cada mutação local
  _serverOrder?: number // incrementa conforme patches do socket aplicados
}

/* ----------------------------- Átomos Jotai ----------------------------- */

// // Lista de conversas
// export const conversationsAtom = atom<IConversationExtended[]>([])

// // Conversa atualmente aberta
// export const activeConversationAtom = atom<IConversationExtended | null>(null)

// type Updater<T> = (prev: T) => T

// export const activeConversationSelector = atom(
//   // get
//   (get) => get(activeConversationAtom),

//   // set
//   (get, set, update) => {
//     // Suporta tanto valor direto quanto updater function
//     const nextActive =
//       typeof update === "function"
//         ? (update as Updater<IConversationExtended | null>)(get(activeConversationAtom))
//         : (update as IConversationExtended | null)

//     set(activeConversationAtom, nextActive)

//     const conversations = get(conversationsAtom)
//     const targetId = nextActive?.id

//     if (targetId != null) {
//       const newConversationsList = conversations.map((conversation) =>
//         conversation.id == targetId ? { ...conversation, ...nextActive } : conversation,
//       )
//       set(conversationsAtom, newConversationsList)
//     }
//   },
// )

// Quantidade de conversas não lidas na Inbox
export const inboxUnreadAtom = atom<number>(0)

// Presença (online/offline) de um contato
export const presenceAtom = atom<
  | {
      workspacePlatformId: any
      jid: any
      status: any
      customerPlatformId: any
      operatorId: any
    }
  | null
  | undefined
>(null)

// Incrementa toda vez que uma task associada à conversa muda
export const conversationTaskUpdatedAtom = atom<number>(0)

export const chatSideWidthAtom = atomWithStorage<number>("chatSideWidth", 270)

// Conversation

export const reconnectedNetworkRefreshAtom = atom<number>(0)

/* ───────── Fonte de verdade ───────── */
export const conversationsByIdAtom = atom<Record<string, IConversationExtended>>({})
export const activeConversationIdAtom = atom<string | null>(null)

/* Views derivadas (somente leitura) */
export const conversationsAtom = atom((get) => Object.values(get(conversationsByIdAtom)))
export const activeConversationAtom = atom((get) => {
  const id = get(activeConversationIdAtom)
  const map = get(conversationsByIdAtom)
  return id ? (map[id] ?? null) : null
})

/* Relógios monotônicos locais (por conveniência global) */
const clientTickAtom = atom(0)

/* Campos alterados localmente desde o último “ack” do servidor (dirty set por ID) */
const dirtyByIdAtom = atom<Record<string, Set<keyof IConversationExtended>>>({})

const pickAnyId = (map: Record<string, IConversationExtended>, exclude: Set<string> = new Set()) => {
  for (const k of Object.keys(map)) if (!exclude.has(k)) return k
  return null
}

const mergeConversation = (old: IConversationExtended, patch: Partial<IConversationExtended>): IConversationExtended => {
  return {
    ...old,
    ...patch,
    customFields: {
      ...(old.customFields ?? {}),
      ...(patch.customFields ?? {}),
    },
    contact: {
      ...(old.contact ?? {}),
      ...(patch.contact ?? {}),
      customFields: {
        ...(old.contact?.customFields ?? {}),
        ...(patch.contact?.customFields ?? {}),
      },
    },
  }
}

/* ───────── Dispatcher (write-only) ───────── */
type Action =
  | { type: "setActive"; id: string | null }
  | { type: "setMany"; items: IConversationExtended[] } // inicial
  | { type: "upsertMany"; items: IConversationExtended[] } // paginação
  | { type: "mergeLocal"; id: string; patch: Partial<IConversationExtended> } // eventos da UI/local
  | { type: "mergeFromSocket"; id: string; patch: Partial<IConversationExtended> } // eventos do socket
  // | { type: "addTag"; id: string; tagId: string }
  // | { type: "removeTag"; id: string; tagId: string }
  // | { type: "clearDirty"; id: string; fields?: (keyof IConversationExtended)[] } // opcional: limpar dirty
  | { type: "remove"; id: string } // 👈 novo
  | { type: "removeMany"; ids: string[] } // 👈 opcional

export const dispatchConversationAtom = atom(null, (get, set, action: Action) => {
  if (action.type === "setActive") {
    set(activeConversationIdAtom, action.id)
    return
  }

  if (action.type === "upsertMany") {
    const byId = { ...get(conversationsByIdAtom) }
    for (const item of action.items) {
      // inicia marcadores locais como 0 se não existirem
      byId[item.id] = {
        _clientOrder: item._clientOrder ?? 0,
        _serverOrder: item._serverOrder ?? 0,
        ...item,
      }
    }
    set(conversationsByIdAtom, byId)
    return
  }

  if (action.type === "setMany") {
    set(
      conversationsByIdAtom,
      action.items.reduce(
        (acc, item) => {
          acc[item.id] = item
          return acc
        },
        {} as Record<string, IConversationExtended>,
      ),
    )
    return
  }

  if (action.type === "mergeLocal") {
    const byId = get(conversationsByIdAtom)
    const cur = byId[action.id]
    if (!cur) return

    // bump de ordem local
    const nextClient = (get(clientTickAtom) || 0) + 1
    set(clientTickAtom, nextClient)

    // marca campos como “dirty”
    // const curDirty = { ...get(dirtyByIdAtom) }
    // const setForId = new Set<keyof IConversationExtended>(curDirty[action.id] ?? [])
    // Object.keys(action.patch).forEach((k) => setForId.add(k as keyof IConversationExtended))
    // curDirty[action.id] = setForId
    // set(dirtyByIdAtom, curDirty)

    const next: IConversationExtended = {
      ...mergeConversation(cur, action.patch),
      _clientOrder: nextClient,
    }
    set(conversationsByIdAtom, { ...byId, [action.id]: next })
    return
  }

  if (action.type === "mergeFromSocket") {
    const byId = get(conversationsByIdAtom)
    const cur = byId[action.id]

    // próxima "ordem do servidor" aplicada
    const nextServer = (cur?._serverOrder ?? 0) + 1

    // 🔹 Se NÃO existe ainda → UPSERT
    if (!cur) {
      const next: IConversationExtended = {
        id: action.id,
        ...action.patch,
        _clientOrder: 0,
        _serverOrder: nextServer,
      }
      set(conversationsByIdAtom, { ...byId, [action.id]: next })
      return
    }

    // 🔹 Existe → respeita dirty/local-wins quando houver conflito
    // const curClient = cur._clientOrder ?? 0

    let safePatch = { ...action.patch }
    // if (nextServer < curClient) {
    //   const dirtySet = get(dirtyByIdAtom)[action.id]
    //   if (dirtySet && dirtySet.size) {
    //     for (const field of dirtySet) {
    //       delete (safePatch as any)[field]
    //     }
    //   }
    // }

    const next: IConversationExtended = {
      ...mergeConversation(cur, safePatch),
      _serverOrder: nextServer,
    }

    set(conversationsByIdAtom, { ...byId, [action.id]: next })
    return
  }

  // if (action.type === "addTag") {
  //   const byId = get(conversationsByIdAtom)
  //   const cur = byId[action.id]
  //   if (!cur) return
  //   const s = new Set(cur.tags ?? [])
  //   s.add(action.tagId)

  //   // trata como mutação LOCAL
  //   set(dispatchConversationAtom, {
  //     type: "mergeLocal",
  //     id: action.id,
  //     patch: { tags: [...s] },
  //   })
  //   return
  // }

  // if (action.type === "removeTag") {
  //   const byId = get(conversationsByIdAtom)
  //   const cur = byId[action.id]
  //   if (!cur) return
  //   const tags = (cur.tags ?? []).filter((t) => t != action.tagId)

  //   // trata como mutação LOCAL
  //   set(dispatchConversationAtom, {
  //     type: "mergeLocal",
  //     id: action.id,
  //     patch: { tags },
  //   })
  //   return
  // }

  if (action.type === "remove") {
    const byId = { ...get(conversationsByIdAtom) }
    const dirty = { ...get(dirtyByIdAtom) }
    const id = action.id

    if (!byId[id]) return // nada a fazer

    delete byId[id]
    delete dirty[id]

    set(conversationsByIdAtom, byId)
    set(dirtyByIdAtom, dirty)

    const activeId = get(activeConversationIdAtom)
    if (activeId == id) {
      set(activeConversationIdAtom, null)
    }
    return
  }

  if (action.type === "removeMany") {
    const byId = { ...get(conversationsByIdAtom) }
    for (const id of action.ids) {
      delete byId[id]
    }
    set(conversationsByIdAtom, byId)
    return
  }

  // if (action.type === "clearDirty") {
  //   const curDirty = { ...get(dirtyByIdAtom) }
  //   if (!curDirty[action.id]) return
  //   if (!action.fields || action.fields.length === 0) {
  //     delete curDirty[action.id]
  //   } else {
  //     for (const f of action.fields) curDirty[action.id]?.delete(f)
  //     if (curDirty[action.id]?.size === 0) delete curDirty[action.id]
  //   }
  //   set(dirtyByIdAtom, curDirty)
  //   return
  // }
})

/* ───────── Shim de compat (opcional): mantém o mesmo nome do seu seletor ───────── */
type Updater<T> = (prev: T) => T
export const activeConversationSelector = atom(
  (get) => get(activeConversationAtom),
  (get, set, update: IConversationExtended | Updater<IConversationExtended | null> | null) => {
    const current = get(activeConversationAtom)
    const next =
      typeof update === "function" ? (update as Updater<IConversationExtended | null>)(current) : (update as IConversationExtended | null)

    const id = next?.id
    if (!id) {
      set(activeConversationIdAtom, null)
      return
    }
    // startTransition(() => {
    // garante existência
    if (!get(conversationsByIdAtom)[id]) {
      set(dispatchConversationAtom, { type: "upsertMany", items: [next!] })
    }
    // aplica como mutação LOCAL (merge)
    set(dispatchConversationAtom, { type: "mergeLocal", id, patch: next! })
    set(activeConversationIdAtom, id)
    // })
  },
)
